import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import { login, setUser } from '@/store/slices/authSlice';
import { useLoginMutation, useGoogleLoginMutation, useAppleLoginMutation, useAdminLoginMutation } from '@/store/api';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { AUTH_ERROR_MESSAGES } from '@/constants/shared/auth';
import { isEmailValid, isPasswordValid, validateLoginForm, getPasswordRequirements } from '@/utils/residents/validation';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { UserType } from '@/types/residents/user';
import { extractErrorMessage } from '@/utils/common/errorHandling';

export type UseLoginFormReturn = {
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  showPassword: boolean;
  toggleShowPassword: () => void;
  isAdminMode: boolean;
  toggleAdminMode: () => void;
  isLoading: boolean;
  canSubmit: boolean;
  submit: () => Promise<void>;
  googleLogin: (credential: string) => Promise<void>;
  appleLogin: (identityToken: string, user?: { email?: string; fullName?: { givenName?: string; familyName?: string } }) => Promise<void>;
  formError: string | null;
  lastInvalidField: 'email' | 'password' | null;
};

export const useLoginForm = (): UseLoginFormReturn => {
  const dispatch = useAppDispatch();
  const { isLoading: authLoading, error: authError } = useAppSelector((state) => state.auth);
  
  // RTK Query hooks
  const [loginMutation, { isLoading: loginLoading }] = useLoginMutation();
  const [googleLoginMutation, { isLoading: googleLoading }] = useGoogleLoginMutation();
  const [appleLoginMutation, { isLoading: appleLoading }] = useAppleLoginMutation();
  const [adminLoginMutation, { isLoading: adminLoginLoading }] = useAdminLoginMutation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);

  const [formError, setFormError] = useState<string | null>(null);
  const [lastInvalidField, setLastInvalidField] = useState<'email' | 'password' | null>(null);
  
  // Combined loading state
  const isLoading = authLoading || loginLoading || googleLoading || appleLoading || adminLoginLoading;

  const validateForm = useCallback((): boolean => {
    return isEmailValid(email) && isPasswordValid(password);
  }, [email, password]);

  const canSubmit = useMemo(() => validateForm() && !isLoading, [validateForm, isLoading]);

  const toggleShowPassword = useCallback(() => setShowPassword((v) => !v), []);
  const toggleAdminMode = useCallback(() => setIsAdminMode((v) => !v), []);

  const submit = useCallback(async () => {
    // Clear any previous error
    setFormError(null);
    setLastInvalidField(null);

    // Client-side validation first
    const validationError = validateLoginForm(email, password);
    if (validationError) {
      setFormError(validationError);
      // Set focus based on which field is invalid
      if (!isEmailValid(email)) {
        setLastInvalidField('email');
      } else if (!isPasswordValid(password)) {
        setLastInvalidField('password');
      }
      return;
    }

    try {
      if (isAdminMode) {
        const result: any = await adminLoginMutation({ email, password }).unwrap();
        const accessToken = result?.response?.accessToken;
        const user = result?.response?.user;
        if (accessToken) {
          await SecureStorage.getInstance().saveToken(accessToken);
        }
        if (user) {
          // Mark authenticated in Redux so RootNavigator routes to Admin
          dispatch(setUser(user));
        }
      } else {
        await dispatch(login({ email, password })).unwrap();
      }
    } catch (err: any) {
      // Unwrap throws the rejectWithValue payload or error
      const message = typeof err === 'string' ? err : err?.message || 'Login failed';
      setFormError(message);
      // Default focus to password for re-entry
      setLastInvalidField('password');
      // Prevent unhandled promise rejection on web by resolving gracefully
      return;
    }
  }, [dispatch, email, password, isAdminMode, validateForm]);

  // Google login function using RTK Query
  const googleLogin = useCallback(async (credential: string) => {
    setFormError(null);
    setLastInvalidField(null);

    try {
      const result = await googleLoginMutation({
        credential,
        isAdmin: isAdminMode,
      }).unwrap();
      
      // Validate response structure
      if (!result?.response?.user || !result?.response?.accessToken) {
        throw new Error('Invalid login response from server');
      }

      const { user, accessToken } = result.response;

      // Validate user type matches login mode
      if (!isAdminMode) {
        // Resident mode: Reject users with admin roles
        if (user.roles && user.roles.length > 0) {
          throw new Error('Admin accounts cannot login to the Resident Portal. Please switch to Admin mode.');
        }

        // For resident users, validate they have a valid lease
        if (user.type === UserType.RESIDENT) {
          // Save token first (needed for lease check API call)
          await SecureStorage.getInstance().saveToken(accessToken);

          // Verify user has valid lease
          const leaseResponse = await AuthAPI.checkLease();
          if (!leaseResponse.success || !leaseResponse.response?.hasLease) {
            // Mirror regular login behavior: clear tokens on invalid lease
            try { 
              await SecureStorage.getInstance().clearTokens(); 
            } catch (clearError) {
              console.error('Failed to clear tokens:', clearError);
            }
            throw new Error('You do not have a valid lease. Please contact your property manager.');
          }
        } else {
          // Non-resident, non-admin users (guests/guarantors)
          await SecureStorage.getInstance().saveToken(accessToken);
        }
      } else {
        // Admin mode: Validate user has admin roles
        if (!user.roles || user.roles.length === 0) {
          throw new Error('This account does not have admin privileges. Please switch to Resident mode.');
        }
        
        // Admin users don't need lease validation - just save the token
        await SecureStorage.getInstance().saveToken(accessToken);

        // Dispatch setUser so adminUISlice initializes selectedOrganizationId/selectedPropertyId
        dispatch(setUser(user));
      }
    } catch (err: any) {
      // Use extractErrorMessage to get the most specific error message (e.g., description field)
      const message = extractErrorMessage(err, 'Google login failed');
      setFormError(message);
      console.error('❌ Google login error:', err);
      throw err; // Re-throw to allow LoginForm to handle the error
    }
  }, [googleLoginMutation, isAdminMode, dispatch]);

  // Apple login function using RTK Query
  const appleLogin = useCallback(async (
    identityToken: string,
    user?: { email?: string; fullName?: { givenName?: string; familyName?: string } }
  ) => {
    setFormError(null);
    setLastInvalidField(null);

    try {
      const result = await appleLoginMutation({
        identityToken,
        isAdmin: isAdminMode,
        user,
      }).unwrap();
      
      // Validate response structure
      if (!result?.response?.user || !result?.response?.accessToken) {
        throw new Error('Invalid login response from server');
      }

      const { user: responseUser, accessToken } = result.response;

      // Validate user type matches login mode
      if (!isAdminMode) {
        // Resident mode: Reject users with admin roles
        if (responseUser.roles && responseUser.roles.length > 0) {
          throw new Error('Admin accounts cannot login to the Resident Portal. Please switch to Admin mode.');
        }

        // For resident users, validate they have a valid lease
        if (responseUser.type === UserType.RESIDENT) {
          // Save token first (needed for lease check API call)
          await SecureStorage.getInstance().saveToken(accessToken);

          // Verify user has valid lease
          const leaseResponse = await AuthAPI.checkLease();
          if (!leaseResponse.success || !leaseResponse.response?.hasLease) {
            // Mirror regular login behavior: clear tokens on invalid lease
            try { 
              await SecureStorage.getInstance().clearTokens(); 
            } catch (clearError) {
              console.error('Failed to clear tokens:', clearError);
            }
            throw new Error('You do not have a valid lease. Please contact your property manager.');
          }
        } else {
          // Non-resident, non-admin users (guests/guarantors)
          await SecureStorage.getInstance().saveToken(accessToken);
        }
      } else {
        // Admin mode: Validate user has admin roles
        if (!responseUser.roles || responseUser.roles.length === 0) {
          throw new Error('This account does not have admin privileges. Please switch to Resident mode.');
        }
        
        // Admin users don't need lease validation - just save the token
        await SecureStorage.getInstance().saveToken(accessToken);

        // Dispatch setUser so adminUISlice initializes selectedOrganizationId/selectedPropertyId
        dispatch(setUser(responseUser));
      }
    } catch (err: any) {
      // Use extractErrorMessage to get the most specific error message (e.g., description field)
      const message = extractErrorMessage(err, 'Apple login failed');
      setFormError(message);
      console.error('❌ Apple login error:', err);
      throw err; // Re-throw to allow LoginForm to handle the error
    }
  }, [appleLoginMutation, isAdminMode, dispatch]);

  // Surface slice-level errors (e.g., if another component dispatched login without unwrap)
  useEffect(() => {
    if (typeof authError === 'string' && authError.length > 0) {
      if (authError === AUTH_ERROR_MESSAGES.NO_AUTH_TOKEN) {
        return;
      }
      setFormError(authError);
    }
  }, [authError]);

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    toggleShowPassword,
    isAdminMode,
    toggleAdminMode,
    isLoading,
    canSubmit,
    submit,
    googleLogin,
    appleLogin,
    formError,
    lastInvalidField,
  };
};


